import {
  Box,
  BoxSx,
  Button,
  Link,
  RadioButton,
  RadioGroup,
  Text,
  ThemeOverride
} from "@modernatx-internal/component-library";
import React from "react";

import { ContentComponentProps, FamilyGuidanceQuizContent } from "@/content-V2/types";

type QuizProps = ContentComponentProps<FamilyGuidanceQuizContent>;
type Step = "age" | "audience" | "result";
type Audience = QuizProps["audienceStep"]["options"][number]["value"];
type Age = QuizProps["ageStep"]["options"][number]["value"];

interface QuizState {
  age?: Age;
  audience?: Audience;
  step: Step;
}

type QuizAction =
  | { audience: Audience; type: "selectAudience" }
  | { age: Age; type: "selectAge" }
  | { type: "back" }
  | { type: "next" }
  | { type: "restart" };

const INITIAL_STATE: QuizState = { step: "audience" };
const NEXT_STEP: Record<Step, Step> = { age: "result", audience: "age", result: "result" };

const canAdvance = ({ age, audience, step }: QuizState): boolean =>
  (step === "audience" && audience !== undefined) || (step === "age" && age !== undefined);

const reducer = (state: QuizState, action: QuizAction): QuizState => {
  switch (action.type) {
    case "selectAudience":
      return {
        ...state,
        age: state.audience === action.audience ? state.age : undefined,
        audience: action.audience
      };
    case "selectAge":
      return { ...state, age: action.age };
    case "next":
      return canAdvance(state) ? { ...state, step: NEXT_STEP[state.step] } : state;
    case "back":
      return state.step === "age" ? { ...state, step: "audience" } : state;
    case "restart":
      return INITIAL_STATE;
  }
};

const stack = (gap: BoxSx["gap"]): BoxSx => ({
  display: "flex",
  flexDirection: "column",
  gap
});

const cardSx: BoxSx = {
  ...stack("spacing-6"),
  borderRadius: "radius-3",
  boxShadow: "shadow-300",
  p: ["spacing-6", null, "spacing-8"]
};

// Soft break points after path slashes keep words whole on narrow screens
const withBreakPoints = (url: string) =>
  (url.match(/[^/]+\/*/g) ?? [url]).map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && <wbr />}
      {part}
    </React.Fragment>
  ));

interface StepHeadingProps {
  children: React.ReactNode;
  headingRef: React.Ref<HTMLElement>;
  id?: string;
}

const StepHeading: React.FC<StepHeadingProps> = ({ children, headingRef, id }) => (
  <Text
    ref={headingRef}
    as="h3"
    id={id}
    sx={{ "&:focus": { outline: "none" } }}
    tabIndex={-1}
    variant={["h5", null, "h4"]}
    weight="bold"
  >
    {children}
  </Text>
);

interface QuestionCardProps<T extends string> {
  actions: React.ReactNode;
  headingRef: React.Ref<HTMLElement>;
  onSelect: (value: T) => void;
  options: { label: React.ReactNode; value: T }[];
  progress: React.ReactNode;
  title: React.ReactNode;
  value?: T;
}

const QuestionCard = <T extends string>({
  actions,
  headingRef,
  onSelect,
  options,
  progress,
  title,
  value
}: QuestionCardProps<T>) => {
  const titleId = React.useId();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = options.find((option) => option.value === event.target.value);

    if (selected) {
      onSelect(selected.value);
    }
  };

  return (
    <Box sx={{ ...cardSx, backgroundColor: "surface-brand-secondary" }}>
      <Box sx={stack("spacing-3")}>
        <Text sx={{ color: "text-secondary" }}>{progress}</Text>
        <StepHeading headingRef={headingRef} id={titleId}>
          {title}
        </StepHeading>
      </Box>
      <Box aria-labelledby={titleId} role="radiogroup">
        <RadioGroup value={value} onChange={handleChange}>
          {options.map((option) => (
            // RadioButton only accepts element labels, so wrap the content in a fragment
            <RadioButton key={option.value} label={<>{option.label}</>} value={option.value} />
          ))}
        </RadioGroup>
      </Box>
      <Box sx={{ display: "flex", gap: "spacing-6" }}>{actions}</Box>
    </Box>
  );
};

interface ShareCardProps {
  message: React.ReactNode;
  result: QuizProps["result"];
}

const ShareCard: React.FC<ShareCardProps> = ({ message, result }) => (
  <Box
    sx={{
      ...stack("spacing-5"),
      backgroundColor: "surface-neutral",
      border: "1px solid",
      borderColor: "stroke-brand",
      borderRadius: "radius-3",
      p: "spacing-5"
    }}
  >
    <Box sx={stack("spacing-3")}>
      <Text as="h4" variant="body-1-bold">
        {result.shareHeading}
      </Text>
      <Box id={result.shareId} sx={stack("spacing-3")}>
        <Text as="div" variant="body-1-regular">
          {message}
        </Text>
        {/* The copy action reads textContent, which has no separator between block siblings */}{" "}
        <Text as="div" sx={{ overflowWrap: "anywhere" }} variant="body-2-regular">
          <Link href={result.shareUrl} sx={{ textDecorationColor: "currentColor" }}>
            {withBreakPoints(result.shareUrl)}
          </Link>
        </Text>
      </Box>
    </Box>
    <Text as="div" variant="body-2-bold">
      {result.copyLink}
    </Text>
  </Box>
);

interface SmallPrintProps {
  children: React.ReactNode;
}

const SmallPrint: React.FC<SmallPrintProps> = ({ children }) => (
  <Text as="div" letterSpacing="kerning-2" sx={{ color: "text-incidental" }} variant="caption">
    {children}
  </Text>
);

interface QuizResultProps {
  headingRef: React.Ref<HTMLElement>;
  onRestart: () => void;
  result: QuizProps["result"];
  variant: QuizProps["result"]["variants"][number];
}

const QuizResult: React.FC<QuizResultProps> = ({ headingRef, onRestart, result, variant }) => (
  <Box
    sx={{
      ...cardSx,
      backgroundColor: "surface-brand",
      color: "text-emphasis",
      gap: "spacing-8"
    }}
  >
    <Box sx={stack("spacing-5")}>
      <Box sx={stack("spacing-3")}>
        <Text variant="body-2-bold">{result.eyebrow}</Text>
        <StepHeading headingRef={headingRef}>{variant.title}</StepHeading>
      </Box>
      <Box sx={stack("spacing-3")}>
        <Text as="div" sx={stack("spacing-2")} variant="body-1-regular">
          {variant.description}
        </Text>
      </Box>
    </Box>
    <ShareCard message={variant.shareMessage} result={result} />
    <Box sx={stack("spacing-6")}>
      <Box sx={stack("spacing-5")}>
        <Text as="h4" variant="h6" weight="bold">
          {result.nextHeading}
        </Text>
        <Box sx={{ display: "grid", gap: "spacing-6", gridTemplateColumns: ["1fr", "1fr 1fr"] }}>
          {variant.actions}
        </Box>
      </Box>
      <Box sx={stack("spacing-2")}>
        <Text as="div" sx={stack("spacing-2")} variant="body-1-regular">
          {variant.postActionsNote}
        </Text>
        <SmallPrint>{result.educationalNote}</SmallPrint>
      </Box>
      <SmallPrint>{result.governmentNote}</SmallPrint>
    </Box>
    <Text as="div" sx={{ textAlign: "center" }} variant="body-2-regular">
      <Link
        sx={{ color: "text-emphasis", textDecorationColor: "currentColor" }}
        onClick={onRestart}
      >
        {result.restartLabel}
      </Link>
    </Text>
  </Box>
);

export const FamilyGuidanceQuiz: React.FC<ContentComponentProps<FamilyGuidanceQuizContent>> = ({
  ageStep,
  audienceStep,
  result
}) => {
  const [state, dispatch] = React.useReducer(reducer, INITIAL_STATE);
  const headingRef = React.useRef<HTMLElement>(null);
  const previousStep = React.useRef(state.step);

  // Move focus to the new heading only after a step change, never on first render
  React.useEffect(() => {
    if (previousStep.current !== state.step) {
      previousStep.current = state.step;
      headingRef.current?.focus();
    }
  }, [state.step]);

  const audienceOption = audienceStep.options.find(({ value }) => value === state.audience);
  const variant = result.variants.find(
    ({ age, audience }) => age === state.age && audience === state.audience
  );

  const renderAdvanceButton = (label: React.ReactNode) => (
    <Button
      disabled={!canAdvance(state)}
      icon="arrow-right"
      iconPosition="end"
      onClick={() => dispatch({ type: "next" })}
    >
      {label}
    </Button>
  );

  return (
    <ThemeOverride enableRoundButtons={false}>
      {state.step === "audience" && (
        <QuestionCard
          actions={renderAdvanceButton(audienceStep.continueLabel)}
          headingRef={headingRef}
          options={audienceStep.options}
          progress={audienceStep.progress}
          title={audienceStep.question}
          value={state.audience}
          onSelect={(audience) => dispatch({ audience, type: "selectAudience" })}
        />
      )}
      {state.step === "age" && audienceOption && (
        <QuestionCard
          actions={
            <>
              <Button variant="outline" onClick={() => dispatch({ type: "back" })}>
                {ageStep.backLabel}
              </Button>
              {renderAdvanceButton(ageStep.resultLabel)}
            </>
          }
          headingRef={headingRef}
          options={ageStep.options}
          progress={ageStep.progress}
          title={audienceOption.ageQuestion}
          value={state.age}
          onSelect={(age) => dispatch({ age, type: "selectAge" })}
        />
      )}
      {state.step === "result" && variant && (
        <QuizResult
          headingRef={headingRef}
          result={result}
          variant={variant}
          onRestart={() => dispatch({ type: "restart" })}
        />
      )}
    </ThemeOverride>
  );
};
